import { defaultLocale } from '@/i18n/locales';
import { formatLocale } from '@/lib/formatLocale';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { createHybridStorage } from './hybridStorage';

// Question IDs only - labels, examples, tips come from translations
const questionIds = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13"];

// Cache for locale-specific store instances
const storeCache = new Map();

// Default locale for backward compatibility - imported from centralized config
const DEFAULT_LOCALE = defaultLocale;

/**
 * Get or create a store instance for the given locale
 * @param {string} locale - The locale code (e.g., 'en-us', 'es-us')
 * @returns {Object} The Zustand store for this locale
 */
export const getBookProStore = (locale = DEFAULT_LOCALE) => {
  if (!storeCache.has(locale)) {
    const store = createLocaleStore(locale);
    storeCache.set(locale, store);
  }
  return storeCache.get(locale);
};

/**
 * Create a locale-specific store instance
 * @param {string} locale - The locale code
 * @returns {Object} The Zustand store
 */
const createLocaleStore = (locale) => create(
  devtools(
    persist(
      (set, get) => ({
        locale, // Store the locale for reference
        contextCode: formatLocale(locale), // Formatted locale for API (e.g., "en_US")
        currentStep: 1,
        formData: {
          name: '',
          gender: '',
          age: '',
          genre: '',
          email: '',
          questionsAndAnswers: [],
          selectedTitle: '',
          selectedSubtitle: '',
          isCustomTitle: false
        },
        selectedTemplateId: 1,
        defaultTemplatePositions: {},
        userTemplatePositions: {},
        processedAuthorImageUrl: null,
        fictionCoverImageUrl: null,
        originalAuthorImageUrl: null,
        praises: [],
        tableOfContents: [],
        sessionId: null,
        questionIds: questionIds,
        visibleTitles: [],
        storedTitles: [],
        imageProcessing: false,
        imageError: null,
        hasSeenEmailScreen: false,
        hasAcceptedTerms: false,
        selectedCoverType: null, // 'fiction' or 'non-fiction'

        // User tracking info
        userIp: null,
        userAgent: null,
        sentryUserId: null,

        // Fiction Cover Generation
        fictionCovers: [],              // Array of { hashKey, url, status, createdAt, style }
        selectedFictionCoverHash: null, // Currently selected cover hash
        selectedFictionStyle: null,     // Currently selected style for generation

        // Screen9 - Order Details
        selectedCopies: 1,
        selectedCoverIndex: 0,
        selectedShippingIndex: 0,
        subtotal: 39.90,
        totalPrice: 39.90,
        selectedCover: 'Paperback',
        lineItems: [],
        shippingRateId: null,

        // Personal message feature
        personalMessage: {
          enabled: false,
          text: '',
        },

        // Custom song feature
        songSelection: {
          enabled: false,
          genre: null,    // 'pop' | 'rock' | 'country' | 'rnb' | 'jazz' | 'rapHipHop'
          voice: null,    // 'female' | 'male'
        },

        // Screen10 - Checkout
        checkoutStatus: null, // null | 'creating' | 'ready' | 'error'
        checkoutError: null,
        stripeSessionId: null,

        initializeSession: (providedSessionId = null) => {
          const state = get()
          // Only create a new session if we don't have one and none was provided
          if (!state.sessionId && !providedSessionId) {
            // New format: locale-timestamp-uuid
            const sessionId = `${locale}-${Date.now()}-${crypto.randomUUID()}`
            set({ sessionId })
          } else if (providedSessionId) {
            // If a sessionId is provided (from URL), set it
            set({ sessionId: providedSessionId })
          }
          // Note: Cloud restoration is now handled separately in the page component
        },

        // Restore session from cloud data
        restoreFromCloud: (cloudData) => {
          if (!cloudData) {
            console.error('Invalid cloud data provided')
            return false
          }

          try {
            // Cloud data can be in two formats:
            // 1. Direct state object (from API): { currentStep, formData, ... }
            // 2. Wrapped with state property: { state: { currentStep, formData, ... } }
            const stateData = cloudData.state ? cloudData.state : cloudData;

            // Validate that we have essential fields
            if (!stateData.formData) {
              console.error('Cloud data missing formData')
              return false
            }


            set({
              ...stateData,
              currentStep: stateData.currentStep >= 10 ? 9 : stateData.currentStep,
              // Generate new session ID with locale prefix
              sessionId: `${locale}-${Date.now()}-${crypto.randomUUID()}`,
              // Ensure contextCode matches current locale, not cloud data
              contextCode: formatLocale(locale),
              // Clear song selection while step is hidden
              songSelection: { enabled: false, genre: null, voice: null },
            })

            console.log('Session restored from cloud', stateData)
            return true
          } catch (error) {
            console.error('Error restoring from cloud:', error)
            return false
          }
        },

        // Use local session (useful when cloud restore fails)
        useLocalSession: () => {
          // The local session is already loaded by Zustand persist
          // This is just a confirmation action
          console.log('Using local session')
          return true
        },

        // Clear current session and start fresh
        clearCurrentSession: () => {
          try {
            // Generate a new session ID with locale prefix
            const sessionId = `${locale}-${Date.now()}-${crypto.randomUUID()}`

            // Reset to initial state
            set({
              currentStep: 1,
              formData: {
                name: '',
                gender: '',
                age: '',
                genre: '',
                email: '',
                questionsAndAnswers: [],
                selectedTitle: '',
                selectedSubtitle: '',
                isCustomTitle: false
              },
              selectedTemplateId: 1,
              defaultTemplatePositions: {},
              userTemplatePositions: {},
              processedAuthorImageUrl: null,
              fictionCoverImageUrl: null,
              praises: [],
              tableOfContents: [],
              visibleTitles: [],
              storedTitles: [],
              imageProcessing: false,
              imageError: null,
              hasSeenEmailScreen: false,
              hasAcceptedTerms: false,
              selectedCoverType: null,
              userIp: null,
              userAgent: null,
              fictionCovers: [],
              selectedFictionCoverHash: null,
              selectedFictionStyle: null,
              sessionId,
              contextCode: formatLocale(locale),
              selectedCopies: 1,
              selectedCoverIndex: 0,
              selectedShippingIndex: 0,
              subtotal: 39.00,
              totalPrice: 39.00,
              selectedCover: 'Paperback',
              lineItems: [],
              shippingRateId: null,
              personalMessage: { enabled: false, text: '' },
              songSelection: { enabled: false, genre: null, voice: null },
              checkoutStatus: null,
              checkoutError: null,
              stripeSessionId: null
            })

            console.log('Session cleared and reset')
            return true
          } catch (error) {
            console.error('Error clearing session:', error)
            return false
          }
        },

        updateFormData: (field, value) => set((state) => ({
          formData: { ...state.formData, [field]: value }
        })),

        addQuestionAndAnswer: (questionId, answer, questionLabel = '') => set((state) => {
          const questionsAndAnswers = state.formData.questionsAndAnswers || [];
          const existing = questionsAndAnswers.find(qa => qa.questionId === questionId);

          if (existing) {
            return {
              formData: {
                ...state.formData,
                questionsAndAnswers: questionsAndAnswers.map(qa =>
                  qa.questionId === questionId ? { ...qa, answer, question: questionLabel || qa.question } : qa
                )
              }
            };
          }
          return {
            formData: {
              ...state.formData,
              questionsAndAnswers: [...questionsAndAnswers, { questionId, answer, question: questionLabel }]
            }
          };
        }),

        removeQuestion: (questionId) => set((state) => ({
          formData: {
            ...state.formData,
            questionsAndAnswers: (state.formData.questionsAndAnswers || []).filter(qa => qa.questionId !== questionId)
          }
        })),

        setSelectedTitle: (title, subtitle, isCustom = false) => set((state) => ({
          formData: {
            ...state.formData,
            selectedTitle: title,
            selectedSubtitle: subtitle,
            isCustomTitle: isCustom
          }
        })),

        setVisibleTitles: (titles) => set({ visibleTitles: titles }),

        setStoredTitles: (titles) => set({ storedTitles: titles }),

        setTitlesFromAPI: (titles) => set({
          visibleTitles: titles.slice(0, 3),
          storedTitles: titles.slice(3)
        }),

        clearTitles: () => set((state) => ({
          visibleTitles: [],
          storedTitles: [],
          formData: {
            ...state.formData,
            selectedTitle: '',
            selectedSubtitle: ''
          }
        })),

        setHasSeenEmailScreen: (value) => set({ hasSeenEmailScreen: value }),

        setHasAcceptedTerms: (value) => set({ hasAcceptedTerms: value }),

        setSelectedCoverType: (coverType) => set({ selectedCoverType: coverType }),

        // User tracking actions
        setUserIp: (ip) => set({ userIp: ip }),
        setUserAgent: (agent) => set({ userAgent: agent }),
        setUserTrackingInfo: (ip, agent) => set({ userIp: ip, userAgent: agent }),
        setSentryUserId: (sentryUserId) => set({ sentryUserId }),

        // Fiction Cover actions
        addFictionCover: (cover) => set((state) => {
          // Prevent duplicates - if hashKey already exists, update instead
          const exists = state.fictionCovers.some(c => c.hashKey === cover.hashKey);
          if (exists) {
            return {
              fictionCovers: state.fictionCovers.map(c =>
                c.hashKey === cover.hashKey ? { ...c, ...cover } : c
              )
            };
          }
          return { fictionCovers: [...state.fictionCovers, cover] };
        }),
        updateFictionCover: (hashKey, updates) => set((state) => ({
          fictionCovers: state.fictionCovers.map(c =>
            c.hashKey === hashKey ? { ...c, ...updates } : c
          )
        })),
        setSelectedFictionCoverHash: (hashKey) => set({ selectedFictionCoverHash: hashKey }),
        setSelectedFictionStyle: (style) => set({ selectedFictionStyle: style }),
        clearFictionCovers: () => set({
          fictionCovers: [],
          selectedFictionCoverHash: null,
          selectedFictionStyle: null
        }),

        setImageProcessing: (processing) => set({ imageProcessing: processing }),

        setImageError: (error) => set({ imageError: error }),

        setSelectedTemplateId: (templateId) => set({ selectedTemplateId: templateId }),

        setDefaultTemplatePositions: (positions) => set({ defaultTemplatePositions: positions }),

        setUserTemplatePositions: (positions) => set((state) => ({
          userTemplatePositions: typeof positions === 'function' ? positions(state.userTemplatePositions) : positions
        })),

        setProcessedAuthorImageUrl: (url) => set({ processedAuthorImageUrl: url }),

        setFictionCoverImageUrl: (url) => set({ fictionCoverImageUrl: url }),
        setOriginalAuthorImageUrl: (url) => set({ originalAuthorImageUrl: url }),

        setPraises: (praises) => set({ praises }),

        setTableOfContents: (tableOfContents) => set({ tableOfContents }),

        clearPraisesAndTOC: () => {
          console.log('Clearing praises and TOC');
          set({
            praises: [],
            tableOfContents: []
          });
        },

        // Screen9 - Order Details actions
        setSelectedCopies: (copies) => set({ selectedCopies: copies }),
        setSelectedCoverIndex: (index) => set({ selectedCoverIndex: index }),
        setSelectedShippingIndex: (index) => set({ selectedShippingIndex: index }),
        setSubtotal: (subtotal) => set({ subtotal }),
        setTotalPrice: (totalPrice) => set({ totalPrice }),
        setSelectedCover: (cover) => set({ selectedCover: cover }),
        setLineItems: (items) => set({ lineItems: items }),
        setShippingRateId: (rateId) => set({ shippingRateId: rateId }),

        // Personal message actions
        setPersonalMessageEnabled: (enabled) => set((state) => ({
          personalMessage: { ...state.personalMessage, enabled }
        })),
        setPersonalMessageText: (text) => set((state) => ({
          personalMessage: { ...state.personalMessage, text }
        })),

        // Custom song actions
        setSongSelection: (data) => set((state) => ({
          songSelection: {
            ...state.songSelection,
            ...data,
            enabled: true,
          }
        })),
        clearSongSelection: () => set(() => ({
          songSelection: {
            enabled: false,
            genre: null,
            voice: null,
          }
        })),
        updateSongSelection: (field, value) => set((state) => ({
          songSelection: {
            ...state.songSelection,
            [field]: value,
          }
        })),

        // Screen10 - Checkout actions
        setCheckoutStatus: (status) => set({ checkoutStatus: status }),
        setCheckoutError: (error) => set({ checkoutError: error }),
        setStripeSessionId: (sessionId) => set({ stripeSessionId: sessionId }),

        // Get complete checkout data (entire store state)
        getCheckoutData: () => {
          const state = get();
          return {
            ...state
          };
        },

        goToNextStep: () => {
          set((state) => {
            let nextStep = state.currentStep + 1;

            // Skip screen 5 (email) if user has already seen it
            if (nextStep === 5 && state.hasSeenEmailScreen) {
              nextStep = 6;
            }

            // Skip screen 10 (song selection) - temporarily hidden
            if (nextStep === 10) {
              nextStep = 11;
            }

            return { currentStep: Math.min(nextStep, 12) };
          })
        },

        goToPreviousStep: () => {
          set((state) => {
            let prevStep = state.currentStep - 1;

            // Skip screen 5 (email) if user has already seen it
            if (prevStep === 5 && state.hasSeenEmailScreen) {
              prevStep = 4;
            }

            // Skip screen 10 (song selection) - temporarily hidden
            if (prevStep === 10) {
              prevStep = 9;
            }

            return { currentStep: Math.max(prevStep, 1) };
          })
        },

        resetForm: () => set((state) => ({
          currentStep: 1,
          formData: {
            name: '',
            gender: '',
            age: '',
            genre: '',
            email: '',
            questionsAndAnswers: [],
            selectedTitle: '',
            selectedSubtitle: '',
            isCustomTitle: false
          },
          selectedTemplateId: 1,
          defaultTemplatePositions: {},
          userTemplatePositions: {},
          processedAuthorImageUrl: null,
          fictionCoverImageUrl: null,
          praises: [],
          tableOfContents: [],
          visibleTitles: [],
          storedTitles: [],
          imageProcessing: false,
          imageError: null,
          hasSeenEmailScreen: false,
          hasAcceptedTerms: false,
          selectedCoverType: null,
          // Preserve user tracking info on reset
          userIp: state.userIp,
          userAgent: state.userAgent,
          // Clear fiction covers on reset
          fictionCovers: [],
          selectedFictionCoverHash: null,
          selectedFictionStyle: null,
          // Generate new session ID with locale prefix
          sessionId: `${locale}-${Date.now()}-${crypto.randomUUID()}`,
          // Ensure contextCode matches current locale
          contextCode: formatLocale(locale),
          // Reset Screen9 data
          selectedCopies: 1,
          selectedCoverIndex: 0,
          selectedShippingIndex: 0,
          subtotal: 39.90,
          totalPrice: 39.90,
          selectedCover: 'Paperback',
          lineItems: [],
          shippingRateId: null,
          personalMessage: { enabled: false, text: '' },
          songSelection: { enabled: false, genre: null, voice: null },
          // Reset Screen10 data
          checkoutStatus: null,
          checkoutError: null,
          stripeSessionId: null
        }))
      }),
      {
        name: `book-pro-storage-${locale}`, // Dynamic storage key per locale
        storage: createJSONStorage(() => createHybridStorage(locale)),
        partialize: (state) => {
          // Exclude questionIds, locale from persistence as they're static/derived
          const { questionIds: _, locale: __, ...rest } = state;
          return rest;
        },
        merge: (persistedState, currentState) => {
          // Ensure questionIds, locale, and contextCode always come from currentState (code), not persisted state
          const merged = {
            ...currentState,
            ...persistedState,
            questionIds: currentState.questionIds,
            locale: currentState.locale,
            contextCode: currentState.contextCode,
          };
          // Temporarily hide song selection step
          if (merged.currentStep === 10) {
            merged.currentStep = 9;
          }
          // Ensure song selection is disabled while step is hidden
          merged.songSelection = { enabled: false, genre: null, voice: null };
          return merged;
        }
      }
    ),
    {
      name: `BookProStore-${locale}`, // Unique name for Redux DevTools
      enabled: process.env.NODE_ENV === 'development' // Only enable in development
    }
  )
);

/**
 * Clear all cached store instances and reset their state.
 * Called after successful purchase to ensure clean slate.
 */
export function clearStoreCache() {
  storeCache.forEach((store) => {
    store.getState().clearCurrentSession();
  });
  storeCache.clear();
}

// Default export for backward compatibility
// Components should migrate to using useLocaleBookProStore hook
const useBookProStore = getBookProStore(DEFAULT_LOCALE);
export default useBookProStore;
